import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowRight, CheckCircle2, AlertCircle } from "lucide-react";
import { postCollaboration } from "../lib/api";
import PageHero from "../components/PageHero";
import { Kicker, Reveal } from "../components/motion";

const EMPTY = { full_name: "", email: "", organization: "", role: "", country: "", collaboration_type: "session", message: "" };

export default function Collaborate() {
  const { t } = useTranslation();
  const ways = t("collaborate.ways", { returnObjects: true });
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function onSubmit(e) {
    e.preventDefault();
    setSubmitting(true); setStatus(null);
    try {
      await postCollaboration(form);
      setStatus("ok");
      setForm(EMPTY);
    } catch {
      setStatus("err");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div data-testid="page-collaborate">
      <PageHero testId="collaborate-hero" kicker={t("collaborate.kicker")} title={t("collaborate.headline")} lead={t("collaborate.lead")} />

      <section className="surface-2" data-testid="collaborate-ways">
        <div className="max-w-[1500px] mx-auto px-6 lg:px-12 py-20 lg:py-28">
          <Reveal>
            <h2 className="font-display text-4xl lg:text-6xl text-ink max-w-3xl leading-[1.02]">{t("collaborate.ways_title")}</h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            {Array.isArray(ways) && ways.map((w, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <div className="gxr-card p-10 h-full" data-testid={`collaborate-way-${i}`}>
                  <div className="font-display text-5xl text-gold leading-none">0{i + 1}</div>
                  <h3 className="font-display text-2xl lg:text-3xl mt-6 leading-tight text-ink">{w.t}</h3>
                  <p className="mt-4 text-slate leading-relaxed">{w.b}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="panel-forest border-y border-line" data-testid="collaborate-form-section">
        <div className="relative max-w-[1200px] mx-auto px-6 lg:px-12 py-20 lg:py-28">
          <Reveal>
            <Kicker light>{t("collaborate.form_kicker")}</Kicker>
            <h2 className="font-display text-4xl lg:text-6xl mt-5 leading-[1.02] text-white">{t("collaborate.form_title")}</h2>
            <p className="mt-5 text-white/75 max-w-2xl text-lg">{t("collaborate.form_body")}</p>
          </Reveal>

          <Reveal delay={0.1}>
            <form onSubmit={onSubmit} className="paper p-8 lg:p-12 space-y-8 mt-12" data-testid="collaborate-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8">
                <div>
                  <label className="gxr-label" htmlFor="c-name">{t("collaborate.form.name")} *</label>
                  <input id="c-name" required value={form.full_name} onChange={set("full_name")} className="gxr-input" data-testid="collab-name-input" />
                </div>
                <div>
                  <label className="gxr-label" htmlFor="c-email">{t("collaborate.form.email")} *</label>
                  <input id="c-email" type="email" required value={form.email} onChange={set("email")} className="gxr-input" data-testid="collab-email-input" />
                </div>
                <div>
                  <label className="gxr-label" htmlFor="c-org">{t("collaborate.form.organization")} *</label>
                  <input id="c-org" required value={form.organization} onChange={set("organization")} className="gxr-input" data-testid="collab-org-input" />
                </div>
                <div>
                  <label className="gxr-label" htmlFor="c-role">{t("collaborate.form.role")}</label>
                  <input id="c-role" value={form.role} onChange={set("role")} className="gxr-input" data-testid="collab-role-input" />
                </div>
                <div>
                  <label className="gxr-label" htmlFor="c-country">{t("collaborate.form.country")}</label>
                  <input id="c-country" value={form.country} onChange={set("country")} className="gxr-input" data-testid="collab-country-input" />
                </div>
                <div>
                  <label className="gxr-label" htmlFor="c-type">{t("collaborate.form.type")} *</label>
                  <select id="c-type" value={form.collaboration_type} onChange={set("collaboration_type")} className="gxr-input bg-transparent" data-testid="collab-type-select">
                    {["session", "opportunities", "program", "other"].map((k) => (
                      <option key={k} value={k}>{t(`collaborate.form.type_options.${k}`)}</option>
                    ))}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="gxr-label" htmlFor="c-msg">{t("collaborate.form.message")} *</label>
                  <textarea id="c-msg" required rows={5} value={form.message} onChange={set("message")} className="gxr-input resize-y" data-testid="collab-message-input" />
                </div>
              </div>

              <div className="flex items-center gap-4 pt-2 flex-wrap">
                <button type="submit" disabled={submitting} className="btn-forest" data-testid="collab-submit-button">
                  {submitting ? t("common.sending") : t("collaborate.cta")} <ArrowRight className="h-4 w-4 rtl:rotate-180" />
                </button>
                {status === "ok" && (
                  <div className="flex items-center gap-2 text-forest" data-testid="collab-success"><CheckCircle2 className="h-5 w-5" /> {t("common.thankYou")}</div>
                )}
                {status === "err" && (
                  <div className="flex items-center gap-2 text-red-600" data-testid="collab-error"><AlertCircle className="h-5 w-5" /> {t("common.errorGeneric")}</div>
                )}
              </div>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
